import { Injectable, computed, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FeedbackAttachmentDraft } from '../types/feedback-attachment-draft';
import { FeedbackCreateRequest } from '../types/feedback-api.types';
import { TechnicalDiagnosticsService } from '../diagnostic/technical-diagnostics.service';
import { RouteMaskService } from '../security/route-mask.service';
import { FeedbackVisibilityService } from './feedback-visibility.service';
import { FeedbackStateStore } from './feedback-state.store';
import {
  FeedbackAttachmentProgressUpdate,
  FeedbackAttachmentUploadResult,
  FeedbackSubmissionService,
  FeedbackSubmissionInput,
} from './feedback-submission.service';
import { FeedbackRefreshService } from './feedback-refresh.service';

@Injectable({
  providedIn: 'root',
})
export class FeedbackFacadeService {
  private readonly stateStore = inject(FeedbackStateStore);
  private readonly technicalDiagnosticsService = inject(TechnicalDiagnosticsService);
  private readonly submissionService = inject(FeedbackSubmissionService);
  private readonly routeMaskService = inject(RouteMaskService);
  private readonly feedbackVisibilityService = inject(FeedbackVisibilityService);
  private readonly translate = inject(TranslateService);
  private readonly feedbackRefreshService = inject(FeedbackRefreshService);

  readonly isOpen = computed(() => this.stateStore.isOpen());
  readonly isSuccess = computed(() => this.stateStore.isSuccess());
  readonly submissionStatus = computed(() => this.stateStore.submissionStatusSnapshot());
  readonly submissionError = computed(() => this.stateStore.submissionErrorSnapshot());

  open(): void {
    this.stateStore.open();
    this.technicalDiagnosticsService.startCapture();
  }

  close(): void {
    this.stateStore.close();
  }

  canShowLauncher(currentUrl: string, restrictedMode: boolean): boolean {
    return !this.routeMaskService.isHiddenRoute(currentUrl) && !this.feedbackVisibilityService.isHidden() && !restrictedMode;
  }

  startDiagnosticsCapture(): void {
    this.technicalDiagnosticsService.startCapture();
  }

  stopDiagnosticsCapture(): void {
    this.technicalDiagnosticsService.stopCapture();
  }

  clearDiagnosticsCapture(): void {
    this.technicalDiagnosticsService.clearCapture();
  }

  async submit(
    input: FeedbackSubmissionInput,
    attachmentDrafts: readonly FeedbackAttachmentDraft[] = [],
    onUploadProgress?: (update: FeedbackAttachmentProgressUpdate) => void,
  ): Promise<{
    valid: boolean;
    errors: readonly string[];
    feedbackPayload: FeedbackCreateRequest;
    backendMessage: string | null;
    createdFeedbackId: number | null;
    attachmentResults: readonly FeedbackAttachmentUploadResult[];
  }> {
    this.stateStore.markSubmitting();
    const result = await this.submissionService.submit(
      input,
      attachmentDrafts,
      () => {
        this.stateStore.markUploadingAttachments();
      },
      onUploadProgress,
    );

    if (!result.valid) {
      this.stateStore.markFailure(this.translate.instant('feedbackWidget.messages.submissionFailedDetail'));
      return result;
    }

    this.stateStore.markSuccess();
    this.feedbackRefreshService.refresh();

    return result;
  }
}
